import React, { useEffect, useState } from "react";
import {
  Modal,
  Box,
  Checkbox,
  FormControlLabel,
  IconButton,
  Select,
  MenuItem,
  SelectChangeEvent,
  styled,
  useTheme,
  InputLabel,
  FormControl,
  ListItemText,
  Typography,
} from "@mui/material";
import { useAlert } from "../../contexts/AlertContext";
import CommonButton from "../CommonButton";
import useResponsive from "../../hooks/useResponsive";

interface DropdownItem {
  label: string;
  value: string;
}

interface TaskDetailsDropdownModalProps {
  modalVisible: boolean;
  handleModalClose: () => void;
  dropdownItems: DropdownItem[];
  photoUploadMultipleTask: (
    selectedItems: string[],
    isCompleteMaterialStatus: boolean
  ) => void;
}

export const StyledModal = styled(Modal)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
}));
interface ModalContentProps {
  maxWidth?: number | string
}
export const ModalContent = styled(Box)<ModalContentProps>(({ theme, maxWidth }) => ({
  backgroundColor: theme.palette.background.paper,
  borderRadius: theme.shape.borderRadius,
  boxShadow: theme.shadows[5],
  padding: theme.spacing(2, 4, 3),
  width: "100%",
  maxWidth: maxWidth,
  position: "relative",
}));
export const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
  padding: theme.spacing(1.5, 3),
  backgroundColor: "white",
  "&.Mui-selected": {
    backgroundColor: theme.palette.greyColors.light,
  },
  "&:hover": {
    backgroundColor: theme.palette.action.hover,
  },
}));
export const CloseButton = styled(IconButton)(({ theme }) => ({
  position: "absolute",
  right: "16px",
  top: "16px",
  zIndex: 1,
}));
const TaskDetailsDropdownModal: React.FC<TaskDetailsDropdownModalProps> = ({
  modalVisible,
  handleModalClose,
  dropdownItems,
  photoUploadMultipleTask,
}) => {
  const { isMobile, isTablet } = useResponsive();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [selectedDropDownItems, setSelectedDropDownItems] = useState<string[]>(
    []
  );
  const [isDropDownShowCheck, setIsDropDownShowCheck] = useState(false);
  const [isCompleteMaterialStatus, setIsCompleteMaterialStatus] =
    useState(false);
  const theme = useTheme();
  const { showAlert } = useAlert();

  const handleSelectionChange = (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value;
    const items = typeof value === "string" ? value.split(",") : value;
    setSelectedDropDownItems(items);
  };

  const handleSave = () => {
    if (isDropDownShowCheck) {
      if (selectedDropDownItems.length > 0) {
        photoUploadMultipleTask(
          selectedDropDownItems,
          isCompleteMaterialStatus
        );
        handleModalClose();
      } else {
        showAlert(
          "Selecteer ten minste één taak uit de lijst om door te gaan.",
          "info"
        );
      }
    } else {
      photoUploadMultipleTask(selectedDropDownItems, isCompleteMaterialStatus);
      handleModalClose();
    }
  };
  useEffect(() => {
    if (!modalVisible) {
      // Reset all states when the modal is closed
      setDropdownOpen(false);
      setSelectedDropDownItems([]);
      setIsCompleteMaterialStatus(false);
    }
  }, [modalVisible]);

  return (
    <StyledModal
      open={modalVisible}
      onClose={handleModalClose}
      aria-labelledby="task-details-modal"
      aria-describedby="task-details-description"
    >
      <ModalContent maxWidth={isMobile ? "90%" : (isTablet ? '70%' : "30%")}>
        <CloseButton
          aria-label="close"
          onClick={handleModalClose}
          sx={{ color: "text.secondary" }}
        >
          <img
            src={"/assets/ic_alert_close.svg"}
            alt={`close-icon`}
            width="16px"
            height="16px"
          />
        </CloseButton>
        <Box mt={4} >
          <FormControl
            fullWidth
          >
            <InputLabel id="demo-simple-select-label">
              Selectie van een element
            </InputLabel>
            <Select
              multiple
              labelId="demo-simple-select-label"
              id="demo-simple-select"
              value={selectedDropDownItems}
              label="Selectie van een element"
              open={dropdownOpen}
              onOpen={() => setDropdownOpen(true)}
              onClose={() => setDropdownOpen(false)}
              onChange={handleSelectionChange}
              renderValue={(selected) => {
                return (
                  <>
                    <Typography sx={{ fontFamily: "Nunito Sans Regular700" }}>
                      {selected.length} taken zijn geselecteerd
                    </Typography>
                  </>
                );
              }}

              MenuProps={{
                PaperProps: {
                  sx: {
                    width: isMobile ? "90%" : (isTablet ? '70%' : "30%"), // Match the width of the FormControl
                    maxWidth: isMobile ? "90%" : (isTablet ? '70%' : "30%") // Prevent exceeding parent width
                  },
                },
              }}
            >
              {dropdownItems.map((item) => (
                <StyledMenuItem
                  key={item.value}
                  value={item.value}
                  selected={selectedDropDownItems.includes(item.value)}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    padding: "8px 16px",
                  }}
                >
                  <ListItemText
                    primary={item.label}
                    sx={{
                      color: "#1D1B20",
                      fontFamily: "Nunito Sans Regular500",
                      flex: 1,
                      // overflow: "hidden",
                      // textOverflow: "ellipsis",
                      // whiteSpace: "nowrap",
                      paddingLeft: "6px",

                      whiteSpace: "normal", // Allow wrapping
                      overflow: "visible", // Remove truncation
                      textOverflow: "clip", // Remove ellipsis
                    }}
                  />
                  <Checkbox
                    checked={selectedDropDownItems.includes(item.value)}
                    sx={{
                      color: theme.palette.primary.main,
                      marginLeft: "auto",
                    }}
                  />
                </StyledMenuItem>
              ))}
            </Select>
          </FormControl>

          {selectedDropDownItems.length > 0 && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={isCompleteMaterialStatus}
                  onChange={(e) => {
                    setIsCompleteMaterialStatus(e.target.checked);
                  }}
                />
              }
              label="Uitgevoerd"
            />
          )}

        </Box>

        <Box mt={2} display="flex" justifyContent="center">
          <CommonButton
            translationKey={"common.button_opslaan"}
            onClick={handleSave}
            fullWidth={false}
            sx={{
              fontFamily: "Nunito Sans Regular500",
              fontWeight: 700,
              width: "105px",
              backgroundColor: "primary.main",
              color: theme.palette.secondary.main,
            }}

          />
        </Box>
      </ModalContent>
    </StyledModal>
  );
};

export default TaskDetailsDropdownModal;
